import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Mic,
  MicOff,
  Loader2,
  Sparkles,
  Upload,
  FileText,
  Image as ImageIcon,
  X,
  ArrowLeft,
  Wand2,
  Check,
  AlertCircle,
  Stethoscope,
  ClipboardList,
  GitCompareArrows,
} from "lucide-react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Consult page.
 * Push-to-talk mic → Whisper transcript → Gemini 3.1 Pro diagnosis w/ file attachments →
 * structured suggestion panel with "Apply all" + per-drug apply into the Petsmate Rx pad.
 */
export default function ConsultPage({ onBack, onApplyToRx }) {  // Inputs
  const [species, setSpecies] = useState("canine");
  const [weight, setWeight] = useState("");
  const [patientName, setPatientName] = useState("");
  const [transcript, setTranscript] = useState("");
  const [files, setFiles] = useState([]); // { file, url, kind }

  // Voice
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [micHint, setMicHint] = useState(null); // { title, detail, showOpenInNewTab }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordStartRef = useRef(0);
  const MIN_RECORDING_MS = 700;

  const inIframe =
    typeof window !== "undefined" ? window.self !== window.top : false;
  const secure =
    typeof window !== "undefined" ? window.isSecureContext !== false : true;
  const hasMediaApi =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

  // AI
  const [diagnosing, setDiagnosing] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [applied, setApplied] = useState({}); // key = "drug-<i>", "dx-<i>", "ddx-<i>", "care-<i>"
  const suggestionIdRef = useRef(null); // stable id shared by every apply-* click for THIS suggestion

  useEffect(() => {
    return () => {
      // Cleanup: stop any active mic stream + revoke object URLs
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      files.forEach((f) => f.url && URL.revokeObjectURL(f.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------ voice input ------------------------------ */

  const startRecording = async () => {
    setMicHint(null);
    if (!hasMediaApi || !secure) {
      const hint = {
        title: !secure
          ? "Mic needs an HTTPS page"
          : "Your browser can't access the mic",
        detail: !secure
          ? "The Web Audio API requires a secure (https://) context. Open the deployed app or use the button below."
          : "This browser doesn't expose navigator.mediaDevices. Try the latest Chrome, Edge, Firefox or Safari, or type the case in the notes box.",
        showOpenInNewTab: !secure || inIframe,
      };
      setMicHint(hint);
      toast.error(hint.title);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick a MIME type the current browser actually supports.
      // Chrome/Firefox → audio/webm;codecs=opus. iOS Safari → audio/mp4.
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/aac",
      ];
      const mimeType =
        candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const type = mr.mimeType || audioChunksRef.current[0]?.type || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type });
        await sendForTranscription(blob, type);
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mediaRecorderRef.current = mr;
      mr.start();
      recordStartRef.current = Date.now();
      setRecording(true);
    } catch (e) {
      console.error("getUserMedia failed", e);
      const name = e?.name || "";
      let hint;
      if (name === "NotAllowedError" || name === "SecurityError") {
        hint = {
          title: inIframe
            ? "Mic blocked inside the preview frame"
            : "Microphone permission denied",
          detail: inIframe
            ? "The preview iframe hasn't been granted microphone access by the parent page. Open Petsmate in its own tab and allow the mic prompt, or just type the case in the notes box below."
            : "Click the lock icon in the address bar → Site settings → allow microphone, then try again. You can also type the case in the notes box below.",
          showOpenInNewTab: true,
        };
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        hint = {
          title: "No microphone found",
          detail: "Plug in a mic (or select the input in your OS sound settings) and try again. Meanwhile you can type the case in the notes box below.",
          showOpenInNewTab: false,
        };
      } else if (name === "NotReadableError") {
        hint = {
          title: "Mic is already in use",
          detail: "Another app / tab is holding the mic. Close it (Zoom, Teams, Meet, Chrome tab, etc.) then try again.",
          showOpenInNewTab: false,
        };
      } else {
        hint = {
          title: "Microphone unavailable",
          detail: e?.message
            ? `Reason: ${e.message}. You can type the case in the notes box below.`
            : "Something went wrong opening the mic. You can type the case in the notes box below.",
          showOpenInNewTab: inIframe,
        };
      }
      setMicHint(hint);
      toast.error(hint.title);
      // Make sure any half-opened stream is closed
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      const elapsed = Date.now() - recordStartRef.current;
      if (elapsed < MIN_RECORDING_MS) {
        // Auto-extend so we always capture a usable clip and Whisper doesn't reject.
        const wait = MIN_RECORDING_MS - elapsed;
        setTimeout(() => {
          try {
            mediaRecorderRef.current?.stop();
          } catch { /* noop */ }
          setRecording(false);
        }, wait);
        return;
      }
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const sendForTranscription = async (blob, blobType) => {
    setTranscribing(true);
    try {
      // Derive a sensible filename extension from the blob's actual mime type.
      // Whisper reads the extension when validating the file.
      const type = (blobType || blob.type || "audio/webm").toLowerCase();
      let ext = "webm";
      if (type.includes("mp4") || type.includes("aac") || type.includes("m4a")) ext = "m4a";
      else if (type.includes("mpeg") || type.includes("mp3")) ext = "mp3";
      else if (type.includes("wav")) ext = "wav";
      else if (type.includes("ogg")) ext = "ogg";
      if (!blob || blob.size < 500) {
        toast.error("Recording too short — hold the mic longer.");
        return;
      }

      const fd = new FormData();
      fd.append("audio", blob, `consult.${ext}`);
      const { data } = await axios.post(`${API_BASE}/consult/transcribe`, fd, {
        timeout: 60000,
      });
      const t = (data?.transcript || "").trim();
      if (!t) {
        toast.error("Couldn't hear anything — try again.");
        return;
      }
      setTranscript((prev) => (prev ? `${prev.trim()} ${t}` : t));
      toast.success("Transcribed");
    } catch (e) {
      console.error(e);
      const backendMsg = e?.response?.data?.detail;
      toast.error(
        typeof backendMsg === "string" && backendMsg.length < 200
          ? backendMsg
          : "Transcription failed. Try typing the case instead."
      );
    } finally {
      setTranscribing(false);
    }
  };

  /* ------------------------------ file uploads ------------------------------ */

  const onFilesSelected = (list) => {
    const arr = Array.from(list || []);
    const accepted = arr.filter((f) =>
      ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(f.type)
    );
    if (accepted.length !== arr.length) {
      toast.error("Only PDF / PNG / JPG / WEBP files are supported.");
    }
    const withMeta = accepted.map((f) => ({
      file: f,
      url: URL.createObjectURL(f),
      kind: f.type.startsWith("image/") ? "image" : "pdf",
    }));
    setFiles((prev) => [...prev, ...withMeta]);
  };

  const removeFile = (i) => {
    setFiles((prev) => {
      const clone = [...prev];
      const [gone] = clone.splice(i, 1);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return clone;
    });
  };

  /* --------------------------------- AI call --------------------------------- */

  const handleDiagnose = async () => {
    const w = Number(weight);
    if (!w || w <= 0 || w > 100) {
      return toast.error("Enter a valid body weight (0.1–100 kg).");
    }
    if (!transcript.trim()) {
      return toast.error("Add symptoms — either record or type them.");
    }
    setDiagnosing(true);
    setSuggestion(null);
    setApplied({});
    suggestionIdRef.current = null;
    try {
      const fd = new FormData();
      fd.append("transcript", transcript);
      fd.append("species", species);
      fd.append("weight_kg", String(w));
      fd.append("patient_name", patientName);
      files.forEach((f) => fd.append("files", f.file, f.file.name));

      const { data } = await axios.post(`${API_BASE}/consult/diagnose`, fd, {
        timeout: 120000,
      });
      suggestionIdRef.current = `ai-${Date.now().toString(36)}`;
      setSuggestion(data);
      toast.success("AI draft ready — review before applying.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "AI consult failed. Try again.");
    } finally {
      setDiagnosing(false);
    }
  };

  /* --------------------------------- apply --------------------------------- */

  const buildApplyPayload = ({
    includeMeds = true,
    includeDx = true,
    includeDdx = true,
    includeCare = true,
    filterIndex = null,
    filterField = null,
  } = {}) => {
    if (!suggestion) return null;
    const disease = {
      id: suggestionIdRef.current || `ai-${Date.now().toString(36)}`,
      name: suggestion.working_diagnosis || "AI-suggested diagnosis",
      species: [species],
      category: "AI Consult",
      clinicalSigns: transcript.trim(),
      diagnostics: suggestion.diagnostics || [],
      differentials: suggestion.differentials || [],
      medications: suggestion.medications || [],
      supportiveCare: suggestion.supportive_care || [],
    };
    return {
      disease,
      species,
      weight: Number(weight),
      patientName,
      transcript,
      includeMeds,
      includeDx,
      includeDdx,
      includeCare,
      filterIndex,
      filterField,
    };
  };

  const applyAll = () => {
    const payload = buildApplyPayload();
    if (!payload) return;
    onApplyToRx(payload, { navigate: true });
    setApplied(markAllApplied(suggestion));
    toast.success("Applied to Rx", { description: "Review, sign & print." });
  };

  const applyOne = (field, i) => {
    // Fresh payload with only that field/item
    const p = buildApplyPayload({
      includeMeds: field === "med",
      includeDx: field === "dx",
      includeDdx: field === "ddx",
      includeCare: field === "care",
      filterIndex: i,
      filterField: field,
    });
    if (!p) return;
    onApplyToRx(p, { navigate: false });
    setApplied((prev) => ({ ...prev, [`${field}-${i}`]: true }));
    toast.success("Added to Rx");
  };

  /* ----------------------------------- ui ----------------------------------- */

  return (
    <div
      data-testid="consult-page"
      className="min-h-screen bg-[#F9F8F6] text-[#1A1F1D]"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="border-b border-[#E2E8F0] bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-from-consult"
              variant="ghost"
              onClick={onBack}
              className="rounded-md hover:bg-[#0F4C3A]/5 hover:text-[#0F4C3A]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Rx
            </Button>
            <Separator orientation="vertical" className="h-6 bg-[#E2E8F0]" />
            <div className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#0F4C3A]" />
              <div
                className="text-lg tracking-tight"
                style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 700 }}
              >
                AI Consult
              </div>
              <Badge className="bg-[#0F4C3A] text-white text-[10px] font-normal ml-1">
                Beta · Gemini 3.1 Pro
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT — inputs */}
        <section className="lg:col-span-5 space-y-5">
          <Card className="border-[#E2E8F0] rounded-xl">
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold">
                  New AI consult
                </div>
                <div
                  className="text-xl tracking-tight mt-1"
                  style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
                >
                  Speak the case
                </div>
                <p className="text-[12px] text-[#5C6B66] mt-1">
                  Hold the mic and describe the patient — signalment, symptoms, exam findings.
                  Attach blood work / imaging if you have any. The AI drafts a working diagnosis,
                  diagnostics, differentials and Rx — you review and sign.
                </p>
              </div>

              {/* Species toggle + weight */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <MicroLabel>Species</MicroLabel>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "canine", l: "Dog" },
                      { v: "feline", l: "Cat" },
                    ].map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        data-testid={`consult-species-${s.v}`}
                        onClick={() => setSpecies(s.v)}
                        className={`px-3 py-2.5 rounded-md text-sm border transition-colors ${
                          species === s.v
                            ? "border-[#0F4C3A] bg-[#0F4C3A] text-white"
                            : "border-[#E2E8F0] bg-white hover:border-[#0F4C3A]/60"
                        }`}
                      >
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <MicroLabel>Body weight (kg)</MicroLabel>
                  <Input
                    data-testid="consult-weight"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="100"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 12.5"
                    className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                  />
                </div>
              </div>

              <div>
                <MicroLabel>Patient name (optional)</MicroLabel>
                <Input
                  data-testid="consult-patient"
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  placeholder="Bruno / Whiskers"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A]"
                />
              </div>

              {/* Mic */}
              <div className="rounded-md border border-[#E2E8F0] bg-[#F9F8F6]/60 p-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid={recording ? "stop-recording" : "start-recording"}
                    onMouseDown={startRecording}
                    onMouseUp={stopRecording}
                    onTouchStart={startRecording}
                    onTouchEnd={stopRecording}
                    disabled={transcribing}
                    className={`relative w-14 h-14 rounded-full grid place-items-center border-2 transition-transform ${
                      recording
                        ? "bg-[#E53E3E] border-[#E53E3E] text-white scale-105 shadow-lg"
                        : "bg-white border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
                    }`}
                    aria-label={recording ? "Release to stop" : "Hold to record"}
                  >
                    {recording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    {recording && (
                      <span className="absolute inset-0 rounded-full animate-ping bg-[#E53E3E]/30" />
                    )}
                  </button>
                  <div className="flex-1">
                    <div className="text-sm font-medium">
                      {recording ? "Recording — release to transcribe" : transcribing ? "Transcribing…" : "Push and hold to speak"}
                    </div>
                    <div className="text-[11px] text-[#5C6B66] mt-0.5">
                      Whisper transcribes in ~2s after you release.
                    </div>
                  </div>
                  {transcribing && <Loader2 className="w-5 h-5 text-[#0F4C3A] animate-spin" />}
                </div>

                {micHint && (
                  <div
                    data-testid="mic-hint"
                    className="mt-3 rounded-md border border-[#E53E3E]/30 bg-[#E53E3E]/5 p-3 text-[12.5px]"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-[#E53E3E] mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-[#1A1F1D]">{micHint.title}</div>
                        <div className="text-[#5C6B66] mt-0.5 leading-relaxed">{micHint.detail}</div>
                        {micHint.showOpenInNewTab && (
                          <button
                            type="button"
                            data-testid="open-in-new-tab"
                            onClick={() => window.open(window.location.href, "_blank", "noopener,noreferrer")}
                            className="mt-2 text-[12px] px-2.5 py-1 rounded-md border border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white transition-colors"
                          >
                            Open Petsmate in a new tab
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <MicroLabel>Transcript / notes</MicroLabel>
                <Textarea
                  data-testid="consult-transcript"
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  placeholder="Type or dictate — signalment, presenting complaint, exam, prior treatment…"
                  className="border-[#E2E8F0] focus-visible:ring-[#0F4C3A] min-h-[120px]"
                />
              </div>

              {/* Files */}
              <div>
                <MicroLabel>Reports / imaging (optional)</MicroLabel>
                <label
                  data-testid="upload-report-label"
                  className="block rounded-md border border-dashed border-[#E2E8F0] p-4 text-center cursor-pointer hover:border-[#0F4C3A]/60 transition-colors"
                >
                  <Upload className="w-4 h-4 mx-auto text-[#0F4C3A]" />
                  <div className="text-sm mt-1">Attach PDFs or X-ray / US images</div>
                  <div className="text-[11px] text-[#5C6B66] mt-0.5">.pdf .png .jpg .webp</div>
                  <input
                    data-testid="report-file-input"
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,image/*"
                    onChange={(e) => onFilesSelected(e.target.files)}
                  />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((f, i) => (
                      <div
                        key={i}
                        data-testid={`report-file-${i}`}
                        className="flex items-center gap-3 border border-[#E2E8F0] rounded-md p-2 bg-white"
                      >
                        {f.kind === "image" ? (
                          <img src={f.url} alt="" className="w-10 h-10 object-cover rounded-md" />
                        ) : (
                          <div className="w-10 h-10 grid place-items-center rounded-md bg-[#0F4C3A]/10 text-[#0F4C3A]">
                            <FileText className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{f.file.name}</div>
                          <div className="text-[11px] text-[#5C6B66]">
                            {(f.file.size / 1024).toFixed(0)} KB · {f.kind === "image" ? "Image" : "PDF"}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label="Remove"
                          className="h-7 w-7 grid place-items-center rounded-md border border-[#E2E8F0] hover:border-[#E53E3E]/60 hover:text-[#E53E3E]"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                data-testid="diagnose-btn"
                onClick={handleDiagnose}
                disabled={diagnosing || transcribing}
                className="w-full h-11 rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white transition-transform duration-150 hover:-translate-y-[1px]"
              >
                {diagnosing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> AI thinking…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" /> Diagnose &amp; Draft Rx
                  </>
                )}
              </Button>

              <div className="text-[11px] text-[#5C6B66] leading-relaxed border-t border-[#E2E8F0] pt-3">
                AI drafts are advisory only. The prescribing veterinarian is responsible for
                verifying contraindications, drug interactions and the patient&apos;s clinical status
                before dispensing.
              </div>
            </CardContent>
          </Card>
        </section>

        {/* RIGHT — suggestion */}
        <section className="lg:col-span-7">
          {suggestion ? (
            <SuggestionPanel
              suggestion={suggestion}
              weight={weight}
              applied={applied}
              onApplyAll={applyAll}
              onApplyOne={applyOne}
              onGoToRx={onBack}
            />
          ) : (
            <EmptyState diagnosing={diagnosing} />
          )}
        </section>
      </main>
    </div>
  );
}

/* ------------------------------ suggestion ui ------------------------------ */

function SuggestionPanel({ suggestion, weight, applied, onApplyAll, onApplyOne, onGoToRx }) {
  const confColour = confidenceColour(suggestion.confidence);
  const anyApplied = Object.values(applied || {}).some(Boolean);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B66] font-semibold">
            AI draft
          </div>
          <div
            className="text-2xl tracking-tight"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
          >
            {suggestion.working_diagnosis || "Suggested working diagnosis"}
          </div>
          <div className="mt-1 flex items-center gap-2">
            {suggestion.confidence && (
              <Badge className={`${confColour} text-[10px] font-normal`}>
                Confidence: {suggestion.confidence}
              </Badge>
            )}
            {weight && (
              <Badge variant="outline" className="border-[#E2E8F0] text-[10px] font-normal">
                {weight} kg
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {anyApplied && (
            <Button
              data-testid="review-rx-btn"
              variant="outline"
              onClick={onGoToRx}
              className="rounded-md border-[#0F4C3A]/40 text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white"
            >
              Review Rx →
            </Button>
          )}
          <Button
            data-testid="apply-all-btn"
            onClick={onApplyAll}
            className="rounded-md bg-[#0F4C3A] hover:bg-[#166B52] text-white"
          >
            <Wand2 className="w-4 h-4 mr-1.5" /> Apply all to Rx
          </Button>
        </div>
      </div>

      {suggestion.reasoning && (
        <Card className="border-[#E2E8F0] rounded-xl">
          <CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6B66] font-semibold mb-1">
              Reasoning
            </div>
            <p className="text-sm leading-relaxed text-[#1A1F1D]">{suggestion.reasoning}</p>
          </CardContent>
        </Card>
      )}

      {/* Diagnostics */}
      <SuggestionSection
        icon={<ClipboardList className="w-4 h-4 text-[#0F4C3A]" />}
        title="Recommended diagnostics"
        empty="No diagnostics suggested."
      >
        <div className="divide-y divide-[#E2E8F0]">
          {(suggestion.diagnostics || []).map((t, i) => (
            <div key={i} className="py-2.5 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-medium">{t.test}</div>
                {t.finding && <div className="text-[12px] text-[#5C6B66] mt-0.5">{t.finding}</div>}
              </div>
              <ApplyPill
                testId={`apply-dx-${i}`}
                done={applied[`dx-${i}`]}
                onClick={() => onApplyOne("dx", i)}
              />
            </div>
          ))}
        </div>
      </SuggestionSection>

      {/* Differentials */}
      <SuggestionSection
        icon={<GitCompareArrows className="w-4 h-4 text-[#0F4C3A]" />}
        title="Differentials to rule out"
        empty="No differentials suggested."
      >
        <div className="flex flex-wrap gap-2">
          {(suggestion.differentials || []).map((d, i) => (
            <button
              key={i}
              type="button"
              data-testid={`apply-ddx-${i}`}
              onClick={() => onApplyOne("ddx", i)}
              className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${
                applied[`ddx-${i}`]
                  ? "border-[#0F4C3A] bg-[#0F4C3A]/10 text-[#0F4C3A]"
                  : "border-[#E2E8F0] bg-[#F9F8F6] hover:border-[#0F4C3A]/60"
              }`}
            >
              {applied[`ddx-${i}`] ? <Check className="w-3 h-3 inline mr-1" /> : null}
              {d}
            </button>
          ))}
        </div>
      </SuggestionSection>

      {/* Medications */}
      <SuggestionSection
        icon={<Stethoscope className="w-4 h-4 text-[#0F4C3A]" />}
        title="Suggested medications"
        empty="No medications suggested."
      >
        <div className="divide-y divide-[#E2E8F0]">
          {(suggestion.medications || []).map((m, i) => {
            const total =
              m.dose_mg_per_kg != null && weight ? `${round(m.dose_mg_per_kg * Number(weight))} mg` : null;
            return (
              <div key={i} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">{m.drug}</div>
                  <div className="text-[12px] text-[#5C6B66] mt-0.5">
                    {m.dose_text || "custom dose"} · {m.route} · {m.frequency} · {m.duration}
                    {total && <span className="text-[#0F4C3A] ml-1">→ {total}</span>}
                  </div>
                  {m.notes && <div className="text-[11px] text-[#5C6B66] mt-0.5">{m.notes}</div>}
                </div>
                <ApplyPill
                  testId={`apply-med-${i}`}
                  done={applied[`med-${i}`]}
                  onClick={() => onApplyOne("med", i)}
                />
              </div>
            );
          })}
        </div>
      </SuggestionSection>

      {/* Care */}
      <SuggestionSection
        icon={<ClipboardList className="w-4 h-4 text-[#0F4C3A]" />}
        title="Care & follow-up"
        empty="No care notes."
      >
        <div className="divide-y divide-[#E2E8F0]">
          {(suggestion.supportive_care || []).map((c, i) => (
            <div key={i} className="py-2.5 flex items-start justify-between gap-3">
              <div className="text-sm text-[#1A1F1D]">{c}</div>
              <ApplyPill
                testId={`apply-care-${i}`}
                done={applied[`care-${i}`]}
                onClick={() => onApplyOne("care", i)}
              />
            </div>
          ))}
        </div>
      </SuggestionSection>
    </div>
  );
}

function EmptyState({ diagnosing }) {
  return (
    <Card className="border-dashed border-[#E2E8F0] rounded-xl">
      <CardContent className="p-10 text-center flex flex-col items-center">
        <div className="w-12 h-12 rounded-full bg-[#0F4C3A]/10 grid place-items-center mb-4">
          {diagnosing ? (
            <Loader2 className="w-5 h-5 text-[#0F4C3A] animate-spin" />
          ) : (
            <Sparkles className="w-5 h-5 text-[#0F4C3A]" />
          )}
        </div>
        <div
          className="text-2xl tracking-tight"
          style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 500 }}
        >
          {diagnosing ? "AI is reviewing the case…" : "The AI draft will appear here"}
        </div>
        <p className="text-sm text-[#5C6B66] mt-2 max-w-md">
          Fill in species + weight, dictate or type the presenting complaint, attach any reports,
          then hit <strong>Diagnose &amp; Draft Rx</strong>. You&apos;ll get diagnostics,
          differentials, medications and care notes you can apply to the Rx pad with one tap.
        </p>
      </CardContent>
    </Card>
  );
}

/* ------------------------------ tiny helpers ------------------------------ */

function ApplyPill({ done, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-[11px] px-2.5 py-1 rounded-md border whitespace-nowrap transition-colors ${
        done
          ? "border-[#0F4C3A] bg-[#0F4C3A]/10 text-[#0F4C3A]"
          : "border-[#E2E8F0] hover:border-[#0F4C3A]/60 hover:text-[#0F4C3A]"
      }`}
    >
      {done ? (
        <>
          <Check className="w-3 h-3 inline mr-1" /> Applied
        </>
      ) : (
        "Apply"
      )}
    </button>
  );
}

function SuggestionSection({ icon, title, children, empty }) {
  const hasChildren = React.Children.toArray(children).some((c) => {
    if (!React.isValidElement(c)) return false;
    const kids = c.props?.children;
    if (Array.isArray(kids)) return kids.length > 0;
    return Boolean(kids);
  });
  return (
    <Card className="border-[#E2E8F0] rounded-xl">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <div
            className="text-sm tracking-[0.12em] uppercase"
            style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: 600 }}
          >
            {title}
          </div>
        </div>
        {hasChildren ? (
          children
        ) : (
          <div className="text-[12px] text-[#5C6B66] italic flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> {empty}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MicroLabel({ children }) {
  return (
    <Label className="text-[10px] uppercase tracking-[0.1em] text-[#5C6B66] font-semibold block mb-1">
      {children}
    </Label>
  );
}

function confidenceColour(c) {
  const v = (c || "").toLowerCase();
  if (v.startsWith("high")) return "bg-[#0F4C3A] text-white";
  if (v.startsWith("mod")) return "bg-[#B7791F] text-white";
  return "bg-[#5C6B66] text-white";
}

function round(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  if (v < 1) return v.toFixed(2);
  if (v < 10) return v.toFixed(1);
  return Math.round(v);
}

function markAllApplied(s) {
  const out = {};
  (s.medications || []).forEach((_, i) => (out[`med-${i}`] = true));
  (s.diagnostics || []).forEach((_, i) => (out[`dx-${i}`] = true));
  (s.differentials || []).forEach((_, i) => (out[`ddx-${i}`] = true));
  (s.supportive_care || []).forEach((_, i) => (out[`care-${i}`] = true));
  return out;
}
